import { Component, Input, OnInit, OnChanges, Output, EventEmitter, HostListener, ElementRef } from '@angular/core';
import { GradeBreakdownData } from './overall-grade-breakdown-chart/overall-grade-breakdown-chart.component';
import { jsPDF } from 'jspdf';
import * as ExcelJS from 'exceljs';
import html2canvas from 'html2canvas';
import "../../../../assets/fonts/SofiaSans-Regular-normal.js";
import "../../../../assets/fonts/Inter_18pt-Regular-normal.js";
import "../../../../assets/fonts/SofiaSans-Bold-normal.js";
import { HitTrackingService } from 'src/app/core/hit-tracking/hit-tracking.service';
import { USER_TRACKING_ACTION_DESCRIPTIONS, USER_TRACKING_ACTIONS, USER_TRACKING_SOURCES } from 'src/app/core/constants/user-tracking.constants';

declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

interface StorePerformanceData {
  salesTotalPerformance: {
    vsTradeAreaSalesAverage: number;
    vsBannerMarketSalesAverage: number;
    vsMarketSalesAverage: number;
    annualSalesTotal: number;
    salesTotalGrade: string;
    salesIndexScore: number;
  };
  salesPerSqftPerformance: {
    vsTradeAreaSalesPerSqftAverage: number;
    vsBannerMarketSalesPerSqftAverage: number;
    vsMarketSalesPerSqftAverage: number;
    annualSalesPerSqft: number;
    salesPerSqftGrade: string;
    salesPerSqftIndexScore: number;
  };
  driveTimeMinutes : number;
}
@Component({
  standalone: false,
  selector: 'mtn-analytics-performance-panel',
  templateUrl: './analytics-performance-panel.component.html',
  styleUrls: ['./analytics-performance-panel.component.scss']
})
export class AnalyticsPerformancePanelComponent implements OnInit, OnChanges {
  @Input() storeData: any = {};
  @Input() uuid: string;
  @Input() banneruuid: string;
  @Input() point : any = {};
  @Input() store : any = {};
  @Input() cbsa : String;
  @Input() isVisible: boolean = true;
  @Input() isFullScreen: boolean = false;
  @Input() isNewPage: boolean = false;
  @Input() assetReviewStoreDetails: any = {};
  @Input() realEstateQualityData: any = {};
  @Input() storePerformanceData: StorePerformanceData;
  @Input() bannerStrengthData: any = {};
  @Input() gradeBreakdownData: any = {};
  @Input() competitiveImpactData: any = {};
  @Input() isAnalyticsLoading: boolean = false;
  @Input() isAnalyticsDataLoading: boolean = false;
  @Input() analyticsLoadingProgress: number = 0;
  @Input() hasAnalyticsError: boolean = false;
  @Input() totalAnalyticsApis: number = 4;
  @Input() dataValidation : boolean = false;
  @Input() loadingdataValidation : any = 0;

  @Output() downloadPdf = new EventEmitter<void>();
  @Output() exportData = new EventEmitter<string>();
  @Output() expandView = new EventEmitter<void>();
  @Output() openExternal = new EventEmitter<void>();
  @Output() assetReviewClose = new EventEmitter<void>();
  @Output() retryAnalytics = new EventEmitter<void>();

  driveTimeRadiusMinutes: any = 'N/A';

  bannerStrengthTooltipHtml = `
  <div style="line-height: 1.6; font-size: 12px; background-color: #000;">
    <strong>Competitive Impact</strong> (also called <strong>Competitive Incursion</strong>):
    Measures the effect competitors have on a store's performance.<br>
    This metric leverages proprietary data from our <strong>Retail Opening and Closing (ROC)</strong> product,
    ensuring a unique and reliable view of competitive dynamics.
  </div>
`;



  exportMenuVisible = false;
  dropdownOpen = false;
  overallGrade ;
  isGradeBreakdownExpanded = false;
  isGlossaryExpanded = false;
  isCompetitorListExpanded = false;
  maxVisibleCompetitors = 3;
  Math = Math; // Make Math available in template

  // Error states - now controlled by parent
  get hasError(): boolean {
    return this.hasAnalyticsError;
  }

  // Default grade breakdown data - only used if no API data
  defaultGradeBreakdownData: GradeBreakdownData = {
    sales: 0,
    spsf: 0,
    realEstate: 0,
    banner: 0,
    competitiveImpact: 0
  };

  gradeColors: Record<string, string> = {
    'A+': '#FFB84D',
    'A': '#32CD32',
    'A-': '#50C878',
    'B+': '#B8E600',
    'B': '#B8E600',
    'B-': '#FFD23C',
    'C+': '#FFA534',
    'C': '#FF7C33',
    'C-': '#FF5C38',
    'D+': '#FC3D3D',
    'D': '#F22626',
    'D-': '#E42222',
    'F': '#DB1E1E',
    'N/A': '#C0C0C0'
  };

  gradeGradients: Record<string, { from: string; to: string }> = {
    'A+': { from: '#42AF5F', to: '#009000' },
    'A': { from: '#50C878', to: '#196719' },
    'A-': { from: '#50C878', to: '#7ED321' },
    'B+': { from: '#7ED321', to: '#B8E600' },
    'B': { from: '#B8E600', to: '#FFCC00' },
    'B-': { from: '#FFCC00', to: '#FFC061' },
    'C+': { from: '#FFB84D', to: '#FFA500' },
    'C': { from: '#FFA500', to: '#FF9500' },
    'C-': { from: '#FF9500', to: '#FA7467' },
    'D+': { from: '#FA7467', to: '#FF4248' },
    'D': { from: '#F05459', to: '#F51D38' },
    'D-': { from: '#E6344A', to: '#FF0033' },
    'F': { from: '#FF0033', to: '#510011' },
    'N/A': { from: '#C0C0C0', to: '#808080' }
  };

  gradeColorMap = [
    { grade: 'A+', color: '#009000', textColor: '#FFFFFF' },
    { grade: 'A', color: '#32CD32', textColor: '#FFFFFF' },
    { grade: 'A-', color: '#50C878', textColor: '#000000' },
    { grade: 'B+', color: '#7ED321', textColor: '#000000' },
    { grade: 'B', color: '#B8E600', textColor: '#000000' },
    { grade: 'B-', color: '#FFCC00', textColor: '#000000' },
    { grade: 'C+', color: '#FFB84D', textColor: '#000000' },
    { grade: 'C', color: '#FFA500', textColor: '#FFFFFF' },
    { grade: 'C-', color: '#FF9500', textColor: '#FFFFFF' },
    { grade: 'D+', color: '#FA7467', textColor: '#000000' },
    { grade: 'D', color: '#F05459', textColor: '#FFFFFF' },
    { grade: 'D-', color: '#E6344A', textColor: '#FFFFFF' },
    { grade: 'F', color: '#FF0033', textColor: '#FFFFFF' },
    { grade: 'N/A', color: '#C0C0C0', textColor: '#000000' }
  ];

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Check if clicked element or any parent has the relevant classes
    const clickedDownloadDropdown = !!target.closest('.download-option, .download-button');
    const clickedExportDropdown = !!target.closest('.export-data-btn');

    const clickedInsideComponent = this.elementRef.nativeElement.contains(target);

    // Close download dropdown if click was outside and not on allowed class
    if (
      this.dropdownOpen &&
      (!clickedInsideComponent || !clickedDownloadDropdown)
    ) {
      this.dropdownOpen = false;
    }

    // Close export dropdown if click was outside and not on allowed class
    if (
      this.exportMenuVisible &&
      (!clickedInsideComponent || !clickedExportDropdown)
    ) {
      this.exportMenuVisible = false;
    }

    console.log('Clicked:', target);
    console.log('dropdownOpen:', this.dropdownOpen, '| exportMenuVisible:', this.exportMenuVisible);
  }

  constructor(
    private elementRef: ElementRef,
     private hitTrackingService: HitTrackingService
  ) {}

  ngOnInit(): void {
    console.log("----------isnewscreen: - ", this.isFullScreen);
    console.log("anwser is here  ----------------------- ",this.storePerformanceData);

    // Set overall grade from real estate data or store data
    this.overallGrade =  this.storeData?.overallGrade || 'N/A';
  }

  ngOnChanges(): void {
    // Update overall grade when data changes
    this.overallGrade =  this.storeData?.overallGrade || 'N/A';
  }

  // Get current grade breakdown data (from parent or default)
  getCurrentGradeBreakdownData(): GradeBreakdownData {
    console.log("🚀 ~ AnalyticsPerformancePanelComponent ~ getCurrentGradeBreakdownData ~  this.gradeBreakdownData:",  this.gradeBreakdownData)
    return this.gradeBreakdownData && Object.keys(this.gradeBreakdownData).length > 0
      ? this.gradeBreakdownData
      : this.defaultGradeBreakdownData;
  }

  getGradeColor(grade: string): string {
    const gradeInfo = this.gradeColorMap.find(item => item.grade === grade);
    return gradeInfo ? gradeInfo.color : '#C0C0C0';
  }

  getGradeTextColor(grade: string): string {
    const gradeInfo = this.gradeColorMap.find(item => item.grade === grade);
    return gradeInfo ? gradeInfo.textColor : '#000000';
  }





  // Get loading state from parent
  get isLoading(): boolean {
    return this.isAnalyticsLoading;
  }

  get isdataLoading(): boolean{
    return this.isAnalyticsDataLoading;
  }

  // Get loading progress from parent
  get loadingProgress(): number {
    return this.analyticsLoadingProgress;
  }

  // Add these methods for other functionalities





  getGradeBackground(grade: string): string {
    const gradient = this.gradeGradients[grade];

  if (!gradient) {
    const fallback = '#F7C948';
    return `linear-gradient(0deg, rgba(255, 255, 255, 0.10), rgba(255, 255, 255, 0.10)), ${fallback}`;
  }

  return `
    linear-gradient(0deg, rgba(255, 255, 255, 0.10), rgba(255, 255, 255, 0.10)),
    linear-gradient(to bottom, ${gradient.from}, ${gradient.to})
  `;
}


  getColorForPercentage(value: string | number): string {
    let numericValue: number;

    if (typeof value === 'number') {
      numericValue = value;
    } else if (typeof value === 'string') {
      const cleaned = value.replace(/[%$]/g, '');
      numericValue = parseFloat(cleaned);

      if (value.trim().startsWith('-')) {
        numericValue *= -1;
      }
    } else {
      return '#FFFFFF';
    }

    // Neutral color for zero values
    if (numericValue === 0) {
      return '#FFFFFF';
    }
    // Custom color for positive/negative
    if (numericValue < 0) {
      return '#FF5E6C';
    } else {
      return '#34C759';
    }
  }

downloadPDF(): void {
  try {
    // Validate jsPDF availability
    if (typeof jsPDF === 'undefined') {
      throw new Error('jsPDF library is not loaded. Please refresh the page and try again.');
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    let yPosition = 20;

    // Constants for consistent spacing
    const SPACING = {
      CONTENT_START: 15,
      SECTION_TITLE: 6,
      TABLE_BOTTOM: 10,
      SECTION_GAP: 8,
      HEADER_HEIGHT: 30,
      FRAME_HEIGHT: 8,
      FOOTER_MARGIN: 20
    };

    // Font sizes
    const FONTS = {
      MAIN_TITLE: 20,
      SECTION_TITLE: 12,
      TABLE_HEADER: 10,
      TABLE_CONTENT: 10,
      DATE: 11
    };

    // =========================
    // Utility Functions
    // =========================

    /**
     * Safely converts any value to string with proper fallback
     * @param value - Value to convert
     * @param camelCase - Whether to apply camel case formatting
     * @param fallback - Custom fallback value (default: '--')
     * @returns Formatted string
     */
    const safeToString = (
  value: any,
  camelCase: boolean = false,
  fallback: string = '--',
  labelMap: Record<string, string> = {}
): string => {
  // Handle null, undefined, empty string
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : fallback;
  }

  // Handle objects
  if (typeof value === 'object') {
    return fallback;
  }

  // Handle numbers
  const num = Number(value);
  if (!isNaN(num) && typeof value === 'number') {
    return num.toFixed(2);
  }

  // Handle strings
  let str = String(value).trim();
  if (!str) {
    return fallback;
  }

  // Check label map first (e.g., A1-excellent → Very Stable)
  if (labelMap[str]) {
    return labelMap[str];
  }

  // Always format hyphenated values like A1-excellent → A1-Excellent
  if (str.includes('-')) {
    const [prefix, suffix] = str.split('-', 2);
    const formattedSuffix = suffix
      ? suffix.charAt(0).toUpperCase() + suffix.slice(1).toLowerCase()
      : '';
    return `${prefix}-${formattedSuffix}`;
  }

  // Standard camel case for non-hyphenated strings (optional)
  if (camelCase) {
    str = str.toLowerCase();
    str = str.charAt(0).toUpperCase() + str.slice(1);
  }

  return str;
};



    /**
     * Safely format percentage values
     */
    const formatPercentage = (value: any, fallback: string = '--'): string => {
      if (value === null || value === undefined || value === '') {
        return fallback;
      }

      const num = Number(value);
      if (isNaN(num)) {
        return fallback;
      }

      const sign = num >= 0 ? '+' : '';
      return `${sign}${num.toFixed(2)}%`;
    };

    /**
     * Safely format currency values
     */
    const formatCurrency = (value: any, fallback: string = '--'): string => {
      if (value === null || value === undefined || value === '') {
        return fallback;
      }

      const num = Number(value);
      if (isNaN(num)) {
        // Check if it's already a formatted string
        const strValue = String(value).trim();
        if (strValue.includes('$') || strValue.includes('M') || strValue.includes('K')) {
          return strValue;
        }
        return fallback;
      }

      return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    };

    /**
     * Get current date in formatted string
     */
    const getCurrentDate = (): string => {
      try {
        const now = new Date();
        const day = now.getDate().toString().padStart(2, '0');
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        const year = now.getFullYear();
        return `${month}-${day}-${year}`;
      } catch (error) {
        console.warn('Error formatting date:', error);
        return 'N/A';
      }
    };

    const currentDate = getCurrentDate();

    // =========================
    // Image Loading Helper
    // =========================
    const loadImage = (imagePath: string, fallbackColor: number[] = [15, 27, 77]): Promise<string | null> => {
      console.log("🚀 ~ AnalyticsPerformancePanelComponent ~ loadImage ~ fallbackColor:", fallbackColor)
      return new Promise((resolve) => {
        try {
          const img = new Image();
          img.onload = () => resolve(imagePath);
          img.onerror = () => {
            console.warn(`Failed to load image: ${imagePath}`);
            resolve(null);
          };
          img.src = imagePath;
        } catch (error) {
          console.warn(`Error loading image: ${imagePath}`, error);
          resolve(null);
        }
      });
    };

    // =========================
    // Header Drawer
    // =========================
    const drawHeader = async (): Promise<number> => {
      try {
        // Dark blue header background
        doc.setFillColor(15, 27, 77);
        doc.rect(0, 0, pageWidth, SPACING.HEADER_HEIGHT, "F");

        // Try to load logo
        const logoPath = "assets/images/rs_logo_blue_white_inline_transparent_cropped.png";
        const logoLoaded = await loadImage(logoPath);

        if (logoLoaded) {
          try {
            doc.addImage(logoLoaded, "PNG", 12, 6, 45, 15);
          } catch (error) {
            console.warn('Error adding logo to PDF:', error);
          }
        }

        // Title (right side)
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(FONTS.MAIN_TITLE);
        doc.setFont("SofiaSans-Bold", "bold");
        doc.text("Asset Review Report", pageWidth - 15, 12, { align: "right" });

        // Date
        doc.setFontSize(FONTS.DATE);
        doc.setFont("SofiaSans-Regular", "normal");
        doc.text(`Generated: ${currentDate}`, pageWidth - 15, 18, { align: "right" });

        // Frame bar
        doc.setFillColor(15, 27, 77);
        doc.rect(0, SPACING.HEADER_HEIGHT, pageWidth, SPACING.FRAME_HEIGHT, "F");

        const framePath = "assets/images/pdf_frame.png";
        const frameLoaded = await loadImage(framePath);

        if (frameLoaded) {
          try {
            doc.addImage(frameLoaded, "PNG", 0, SPACING.HEADER_HEIGHT, pageWidth, SPACING.FRAME_HEIGHT);
          } catch (error) {
            console.warn('Error adding frame to PDF:', error);
          }
        }

        return SPACING.HEADER_HEIGHT + SPACING.FRAME_HEIGHT + SPACING.CONTENT_START;
      } catch (error) {
        console.error('Error drawing header:', error);
        return SPACING.HEADER_HEIGHT + SPACING.FRAME_HEIGHT + SPACING.CONTENT_START;
      }
    };

    // =========================
    // Footer Drawer
    // =========================
    const drawFooter = async (pageNum: number, totalPages: number): Promise<void> => {
      try {
        const footerY = pageHeight - SPACING.FRAME_HEIGHT;

        doc.setFillColor(255, 255, 255);
        doc.rect(0, footerY, pageWidth, SPACING.FRAME_HEIGHT, "F");

        const framePath = "assets/images/pdf_frame.png";
        const frameLoaded = await loadImage(framePath);

        if (frameLoaded) {
          try {
            doc.addImage(frameLoaded, "PNG", 0, footerY, pageWidth, SPACING.FRAME_HEIGHT);
          } catch (error) {
            console.warn('Error adding footer frame:', error);
          }
        }

        // Page numbers
        doc.setTextColor(100, 100, 100);
        doc.setFontSize(9);
        doc.setFont("Inter_18pt-Regular", "normal");
        doc.text(`${pageNum} / ${totalPages}`, pageWidth / 2, footerY - 2, { align: "center" });
      } catch (error) {
        console.error('Error drawing footer:', error);
      }
    };

    // =========================
    // Column width mapping
    // =========================
    const COLUMN_WIDTHS: { [key: string]: number[] } = {
      storeInfo: [50, pageWidth - 90],
      performance: [66, pageWidth - 105],
      salesSqft: [66, pageWidth - 105],
      realEstate: [65, pageWidth - 105],
      banner: [65, pageWidth - 105],
      competitive: [60, 35, pageWidth - 135]
    };

    // =========================
    // Optimized table function
    // =========================
    const drawCustomTable = (
      startY: number,
      headers: string[],
      data: any[][],
      title?: string,
      tableType: string = 'default'
    ): number => {
      try {
        let currentY = startY;
        const tableX = 20;
        const tableWidth = pageWidth - 40;
        const radius = 1;
        const headerHeight = 11;
        const minRowHeight = 9;
        const cellPadding = 4;
        const lineHeight = 3.5;

        // Validate inputs
        if (!headers || headers.length === 0) {
          console.warn('No headers provided for table');
          return currentY;
        }

        if (!data || data.length === 0) {
          console.warn('No data provided for table');
          doc.setFontSize(FONTS.TABLE_CONTENT);
          doc.setTextColor(100, 100, 100);
          doc.setFont("Inter_18pt-Regular", "normal");
          doc.text('No data available', tableX, currentY);
          return currentY + 10;
        }

        // Get predefined column widths
        const colWidths = COLUMN_WIDTHS[tableType] ||
          new Array(headers.length).fill(tableWidth / headers.length);

        // Text splitting helper
        const splitTextToLines = (text: string, maxWidth: number): string[] => {
          try {
            doc.setFontSize(FONTS.TABLE_CONTENT);
            doc.setFont("Inter_18pt-Regular", "normal");
            
            // Use jsPDF's built-in text splitting which handles width properly
            const splitText = doc.splitTextToSize(text, maxWidth - 2); // Subtract 2 for safety margin
            return Array.isArray(splitText) ? splitText : [splitText];
          } catch (error) {
            console.warn('Error splitting text:', error);
            return [text];
          }
        };

        // Section title
        if (title) {
          doc.setFontSize(FONTS.SECTION_TITLE);
          doc.setFont("SofiaSans-Bold", "bold");
          doc.setTextColor(0, 0, 0);
          doc.text(title, tableX, currentY);
          currentY += SPACING.SECTION_TITLE;
        }

        const tableStartY = currentY;

        // Header with rounded top corners
        doc.setFillColor(133, 51, 153);
        doc.roundedRect(tableX, currentY, tableWidth, headerHeight, radius, radius, "F");
        doc.setFillColor(133, 51, 153);
        doc.rect(tableX, currentY + radius, tableWidth, headerHeight - radius, "F");

        // Header text
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(FONTS.TABLE_HEADER);
        doc.setFont("SofiaSans-Regular", "normal");
        let currentX = tableX;
        headers.forEach((header, index) => {
          const headerText = safeToString(header, false, 'Column');
          doc.text(headerText, currentX + cellPadding, currentY + 6);
          currentX += colWidths[index];
        });

        currentY += headerHeight;

        // Data rows with dynamic height
        data.forEach((row, rowIndex) => {
          try {
            let maxLinesInRow = 1;
            const rowLines: string[][] = [];
            currentX = tableX;

            // Ensure row has same length as headers
            const paddedRow = [...row];
            while (paddedRow.length < headers.length) {
              paddedRow.push('--');
            }

            paddedRow.forEach((cell, cellIndex) => {
              const cellText = safeToString(cell, false, '--');
              const availableWidth = colWidths[cellIndex] - (cellPadding * 2);
              const lines = splitTextToLines(cellText, availableWidth);
              rowLines.push(lines);
              maxLinesInRow = Math.max(maxLinesInRow, lines.length);
            });

            const isMultiLine = maxLinesInRow > 1;
            const extraBottomSpace = isMultiLine ? 1.5 : 0;
            const rowHeight = Math.max(minRowHeight, maxLinesInRow * lineHeight + cellPadding + extraBottomSpace);

            // Row background - alternating colors
            const bgColor = rowIndex % 2 === 0 ? 255 : 248;
            doc.setFillColor(bgColor, bgColor, bgColor);
            doc.rect(tableX, currentY, tableWidth, rowHeight, "F");

            // Cell content
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(FONTS.TABLE_CONTENT);
            currentX = tableX;

            paddedRow.forEach((cell, cellIndex) => {
              console.log("🚀 ~ AnalyticsPerformancePanelComponent ~ drawCustomTable ~ cell:", cell)
              try {
                doc.setFont("Inter_18pt-Regular", "normal");
                const lines = rowLines[cellIndex];

                lines.forEach((line, lineIndex) => {
                  const textY = currentY + cellPadding + (lineIndex * lineHeight) + 2.5;
                  doc.text(line, currentX + cellPadding, textY);
                });

                currentX += colWidths[cellIndex];
              } catch (cellError) {
                console.warn(`Error rendering cell [${rowIndex}][${cellIndex}]:`, cellError);
                currentX += colWidths[cellIndex];
              }
            });

            currentY += rowHeight;
          } catch (rowError) {
            console.warn(`Error rendering row ${rowIndex}:`, rowError);
            currentY += minRowHeight;
          }
        });

        const tableHeight = currentY - tableStartY;

        // Table borders
        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.3);
        doc.roundedRect(tableX, tableStartY, tableWidth, tableHeight, radius, radius, "S");

        // Column separators
        currentX = tableX;
        for (let i = 0; i < headers.length - 1; i++) {
          currentX += colWidths[i];
          doc.line(currentX, tableStartY, currentX, tableStartY + tableHeight);
        }

        // Row separators
        let rowY = tableStartY + headerHeight;
        data.forEach((row, rowIndex) => {
          if (rowIndex < data.length - 1) {
            try {
              let maxLinesInRow = 1;
              row.forEach((cell, cellIndex) => {
                const cellText = safeToString(cell, false, '--');
                const availableWidth = colWidths[cellIndex] - (cellPadding * 2);
                const lines = splitTextToLines(cellText, availableWidth);
                maxLinesInRow = Math.max(maxLinesInRow, lines.length);
              });

              const isMultiLine = maxLinesInRow > 1;
              const extraBottomSpace = isMultiLine ? 1.5 : 0;
              const rowHeight = Math.max(minRowHeight, maxLinesInRow * lineHeight + cellPadding + extraBottomSpace);
              rowY += rowHeight;
              doc.setDrawColor(240, 240, 240);
              doc.setLineWidth(0.2);
              doc.line(tableX, rowY, tableX + tableWidth, rowY);
            } catch (separatorError) {
              console.warn(`Error drawing row separator ${rowIndex}:`, separatorError);
            }
          }
        });

        return currentY + SPACING.TABLE_BOTTOM;
      } catch (error) {
        console.error('Error drawing table:', error);
        return startY + 20; // Return minimal offset on error
      }
    };

    // =========================
    // Smart page management
    // =========================
    const checkNewPage = async (requiredSpace: number, forceNewPage: boolean = false): Promise<number> => {
      try {
        if (forceNewPage || yPosition + requiredSpace > pageHeight - SPACING.FOOTER_MARGIN) {
          doc.addPage();
          yPosition = await drawHeader();
        }
        return yPosition;
      } catch (error) {
        console.error('Error checking new page:', error);
        return yPosition;
      }
    };

    // =========================
    // Safe data extraction helpers
    // =========================
    const getSafeValue = (value: any, fallback: any = '--'): any => {
      return (value !== null && value !== undefined && value !== '') ? value : fallback;
    };

    // const getAllCompetitors = (): any[] => {
    //   try {
    //     if (!this.competitiveImpactData) {
    //       return [];
    //     }

    //     const competitors: any[] = [];

    //     // Safely extract competitors from various possible structures
    //     if (Array.isArray(this.competitiveImpactData.competitors)) {
    //       competitors.push(...this.competitiveImpactData.competitors);
    //     }

    //     if (Array.isArray(this.competitiveImpactData.nearbyCompetitors)) {
    //       competitors.push(...this.competitiveImpactData.nearbyCompetitors);
    //     }

    //     return competitors.filter(c => c && (c.name || c.address)); // Filter out invalid entries
    //   } catch (error) {
    //     console.warn('Error extracting competitors:', error);
    //     return [];
    //   }
    // };

    // =========================
    // Grade Breakdown Chart Capture
    // =========================
    const captureGradeBreakdownChart = async (): Promise<string | null> => {
      try {
        // Find existing chart element without expanding UI
        let chartElement = document.querySelector('mtn-overall-grade-breakdown-chart') as HTMLElement;
        
        if (!chartElement) {
          // If not found, temporarily expand silently in background
          const wasExpanded = this.isGradeBreakdownExpanded;
          this.isGradeBreakdownExpanded = true;
          
          // Force immediate DOM update without animation
          const breakdownWrapper = document.querySelector('.breakdown-content-wrapper') as HTMLElement;
          if (breakdownWrapper) {
            breakdownWrapper.style.transition = 'none';
            breakdownWrapper.style.maxHeight = '500px';
            breakdownWrapper.style.opacity = '1';
          }
          
          await new Promise(resolve => setTimeout(resolve, 50));
          chartElement = document.querySelector('mtn-overall-grade-breakdown-chart') as HTMLElement;
          
          if (chartElement) {
            // Change text colors to black and force vertical legend for PDF
            const textElements = chartElement.querySelectorAll('.legend-label, text, .chart-text, span, div');
            const originalColors: string[] = [];
            const legendElement = chartElement.querySelector('.chart-legend');
            const chartContentElement = chartElement.querySelector('.chart-content');
            const originalLegendStyle = legendElement ? legendElement.getAttribute('style') || '' : '';
            const originalContentStyle = chartContentElement ? chartContentElement.getAttribute('style') || '' : '';
            
            textElements.forEach((el, index) => {
              const element = el as HTMLElement;
              originalColors[index] = element.style.color || '';
              element.style.color = '#000000';
            });
            
            // Force vertical legend layout for PDF
            if (legendElement) {
              (legendElement as HTMLElement).style.flexDirection = 'column';
              (legendElement as HTMLElement).style.alignItems = 'flex-start';
              (legendElement as HTMLElement).style.gap = '16px';
            }
            if (chartContentElement) {
              (chartContentElement as HTMLElement).style.flexDirection = 'row';
              (chartContentElement as HTMLElement).style.gap = '60px';
            }
            
            const canvas = await html2canvas(chartElement, {
              backgroundColor: '#ffffff',
              scale: 2,
              useCORS: true,
              allowTaint: true,
              logging: false
            });
            
            // Restore original colors and layout
            textElements.forEach((el, index) => {
              const element = el as HTMLElement;
              element.style.color = originalColors[index];
            });
            
            // Restore original legend layout
            if (legendElement) {
              legendElement.setAttribute('style', originalLegendStyle);
            }
            if (chartContentElement) {
              chartContentElement.setAttribute('style', originalContentStyle);
            }
            
            // Restore state immediately
            this.isGradeBreakdownExpanded = wasExpanded;
            if (breakdownWrapper) {
              breakdownWrapper.style.transition = '';
              if (!wasExpanded) {
                breakdownWrapper.style.maxHeight = '0';
                breakdownWrapper.style.opacity = '0';
              }
            }
            
            return canvas.toDataURL('image/png');
          }
          
          // Restore if failed
          this.isGradeBreakdownExpanded = wasExpanded;
          if (breakdownWrapper) {
            breakdownWrapper.style.transition = '';
          }
        } else {
          // Chart is already visible, capture directly
          const textElements = chartElement.querySelectorAll('.legend-label, text, .chart-text, span, div');
          const originalColors: string[] = [];
          const legendElement = chartElement.querySelector('.chart-legend');
          const chartContentElement = chartElement.querySelector('.chart-content');
          const originalLegendStyle = legendElement ? legendElement.getAttribute('style') || '' : '';
          const originalContentStyle = chartContentElement ? chartContentElement.getAttribute('style') || '' : '';
          
          textElements.forEach((el, index) => {
            const element = el as HTMLElement;
            originalColors[index] = element.style.color || '';
            element.style.color = '#000000';
          });
          
          // Force vertical legend layout for PDF
          if (legendElement) {
            (legendElement as HTMLElement).style.flexDirection = 'column';
            (legendElement as HTMLElement).style.alignItems = 'flex-start';
            (legendElement as HTMLElement).style.gap = '16px';
          }
          if (chartContentElement) {
            (chartContentElement as HTMLElement).style.flexDirection = 'row';
            (chartContentElement as HTMLElement).style.gap = '60px';
          }
          
          const canvas = await html2canvas(chartElement, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: false
          });
          
          // Restore original colors and layout
          textElements.forEach((el, index) => {
            const element = el as HTMLElement;
            element.style.color = originalColors[index];
          });
          
          // Restore original legend layout
          if (legendElement) {
            legendElement.setAttribute('style', originalLegendStyle);
          }
          if (chartContentElement) {
            chartContentElement.setAttribute('style', originalContentStyle);
          }
          
          return canvas.toDataURL('image/png');
        }
        
        return null;
      } catch (error) {
        console.warn('Error capturing grade breakdown chart:', error);
        return null;
      }
    };

    // =========================
    // Main PDF Generation
    // =========================
    const generatePDFContent = async (): Promise<void> => {
      // Draw header on page 1
      yPosition = await drawHeader();

      // =========================
      // PAGE 1: Store Information + Performance Metrics
      // =========================

      // Store Information Section
      const storeInfo = [
        ["Store Name", safeToString(this.assetReviewStoreDetails?.storeName , false, 'N/A')],
        ["Address", safeToString(this.assetReviewStoreDetails?.address, false, 'N/A')],
        ["Trade Area", safeToString(this.assetReviewStoreDetails?.tradeAreaName , false, 'N/A')],
        ["Report Date", safeToString(this.assetReviewStoreDetails?.reportDate, false, 'N/A')],
        ["Overall Grade", safeToString(this.overallGrade, false, 'N/A')]
      ];

      // Section title
      doc.setFontSize(FONTS.SECTION_TITLE);
      doc.setFont("SofiaSans-Bold", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text("Store Information", 20, yPosition);
      yPosition += SPACING.SECTION_TITLE;

      yPosition = drawCustomTable(yPosition, ["Field", "Details"], storeInfo, undefined, 'storeInfo');
      yPosition += SPACING.SECTION_GAP;

      // Performance Metrics Section
      doc.setFontSize(FONTS.SECTION_TITLE);
      doc.setFont("SofiaSans-Bold", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text("Total Sales Performance", 20, yPosition);
      yPosition += SPACING.SECTION_TITLE;

      const totalSalesData = [
        ["Grade", safeToString(this.storePerformanceData?.salesTotalPerformance?.salesTotalGrade, false, 'N/A')],
        ["Subject Store Annual Sales", formatCurrency(Math.round(this.storePerformanceData?.salesTotalPerformance?.annualSalesTotal))],
        [`vs Trade Area Average ${ this.storePerformanceData?.driveTimeMinutes ? '(' + this.storePerformanceData?.driveTimeMinutes + ' min DT)' : '' }`, formatPercentage(this.storePerformanceData?.salesTotalPerformance?.vsTradeAreaSalesAverage)],
        ["vs Banner Market Average", formatPercentage(this.storePerformanceData?.salesTotalPerformance?.vsBannerMarketSalesAverage)],
        ["vs Market Average", formatPercentage(this.storePerformanceData?.salesTotalPerformance?.vsMarketSalesAverage)],
      ];

      yPosition = drawCustomTable(yPosition, ["Performance Metric", "Result"], totalSalesData, undefined, 'performance');

      // =========================
      // PAGE 2: Sales Per SqFt + Real Estate Quality + Banner Strength
      // =========================
      yPosition = await checkNewPage(0, true);

      // Sales Per Square Foot Section
      const salesSqftData = [
        ["Grade", safeToString(this.storePerformanceData?.salesPerSqftPerformance?.salesPerSqftGrade, false, 'N/A')],
        ["Subject Store Annual Sales PSF", formatCurrency(Math.round(this.storePerformanceData?.salesPerSqftPerformance?.annualSalesPerSqft))],
        [`vs Trade Area Average ${ this.storePerformanceData?.driveTimeMinutes ? '(' + this.storePerformanceData?.driveTimeMinutes + ' min DT)' : '' }`, formatPercentage(this.storePerformanceData?.salesPerSqftPerformance?.vsTradeAreaSalesPerSqftAverage)],
        ["vs Banner Market Average", formatPercentage(this.storePerformanceData?.salesPerSqftPerformance?.vsBannerMarketSalesPerSqftAverage)],
        ["vs Market Average", formatPercentage(this.storePerformanceData?.salesPerSqftPerformance?.vsMarketSalesPerSqftAverage)],
      ];

      yPosition = drawCustomTable(yPosition, ["Performance Metric", "Result"], salesSqftData, "Sales Per Square Foot", 'salesSqft');
      yPosition += SPACING.SECTION_GAP;

      // Real Estate Quality Section
      const realEstateData = [
        ["Grade", safeToString(this.realEstateQualityData?.grade, true, 'N/A')],
        ["Accessibility", safeToString(this.realEstateQualityData?.accessibility, true, 'N/A')],
        ["Parking", safeToString(this.realEstateQualityData?.parking, true, 'N/A')],
        ["Visibility", safeToString(this.realEstateQualityData?.visibility, true, 'N/A')],
        ["Traffic Flow", safeToString(this.realEstateQualityData?.trafficFlow, true, 'N/A')],
        ["Shopping Center Condition", safeToString(this.realEstateQualityData?.scCondition, true, 'N/A')]
      ];

      yPosition = drawCustomTable(yPosition, ["Performance Metric", "Result"], realEstateData, "Real Estate Quality", 'realEstate');
      yPosition += SPACING.SECTION_GAP;

      // Banner Strength Section
      const bannerStrengthDataExport = [
        ["Grade", safeToString(this.bannerStrengthData?.grade, false, 'N/A')],
        ["Credit Score", safeToString(this.bannerStrengthData?.creditRating, false, 'N/A')],
        ["Network Stability", safeToString(this.bannerStrengthData?.networkStabillity, false, 'N/A')],
        ["Market Presence", safeToString(this.bannerStrengthData?.marketPresence, false, 'N/A')]
      ];

      yPosition = drawCustomTable(yPosition, ["Performance Metric", "Result"], bannerStrengthDataExport, "Banner Strength", 'banner');

      // =========================
      // PAGE 4+: Competitive Impact
      // =========================
      yPosition = await checkNewPage(0, true);

      const competitiveGrade = getSafeValue(this.competitiveImpactData?.competitiveImpactGrade, 'N/A');

      // Section title
      doc.setFontSize(FONTS.SECTION_TITLE);
      doc.setFont("SofiaSans-Bold", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text(
  `Competitive Impact${
   this.getAllCompetitors().length > 0 ? ` (${this.driveTimeRadiusMinutes} min DT)` : ''
  } - Grade: ${competitiveGrade}`,
  20,
  yPosition
);

      yPosition += SPACING.SECTION_TITLE;

      // Prepare competitors data
      const allCompetitors = this.getAllCompetitors();
      console.log("🚀 ~ AnalyticsPerformancePanelComponent ~ generatePDFContent ~ allCompetitors:", allCompetitors)
      const competitorsData = allCompetitors.map(competitor => [
        safeToString(getSafeValue(competitor?.name), false, 'N/A'),
        safeToString(getSafeValue(competitor?.distance), false, 'N/A'),
        safeToString(getSafeValue(competitor?.address), false, 'N/A')
      ]);
      console.log("🚀 ~ AnalyticsPerformancePanelComponent ~ generatePDFContent ~ competitorsData:", competitorsData)

      if (competitorsData.length > 0) {
        const COMPETITORS_PER_PAGE = 12;
        const totalCompetitorPages = Math.ceil(competitorsData.length / COMPETITORS_PER_PAGE);

        for (let pageIndex = 0; pageIndex < totalCompetitorPages; pageIndex++) {
          if (pageIndex > 0) {
            yPosition = await checkNewPage(0, true);
            doc.setFontSize(FONTS.SECTION_TITLE);
            doc.setFont("SofiaSans-Bold", "bold");
            doc.setTextColor(0, 0, 0);
            doc.text(`Competitive Impact - Continued`, 20, yPosition);
            yPosition += SPACING.SECTION_TITLE;
          }

          const startIndex = pageIndex * COMPETITORS_PER_PAGE;
          const endIndex = Math.min(startIndex + COMPETITORS_PER_PAGE, competitorsData.length);
          const pageCompetitors = competitorsData.slice(startIndex, endIndex);

          yPosition = drawCustomTable(yPosition, ["Competitor", "Distance", "Address"], pageCompetitors, undefined, 'competitive');
        }
      } else {
        doc.setFontSize(FONTS.TABLE_CONTENT);
        doc.setTextColor(100, 100, 100);
        doc.setFont("Inter_18pt-Regular", "normal");
        yPosition = yPosition + SPACING.SECTION_GAP/2;
        doc.text('No competitive data available', 20, yPosition);
        yPosition = yPosition + SPACING.SECTION_GAP-2;
        doc.text(`Drive time : ${this.driveTimeRadiusMinutes} min`, 20, yPosition);
      }
      // =========================
      // Grade Breakdown Chart (after competitive impact)
      // =========================
      yPosition += SPACING.SECTION_GAP * 2;
      yPosition = await checkNewPage(120); // Check if we need space for chart

      // Overall Grade Breakdown Chart Section
      const chartImage = await captureGradeBreakdownChart();
      if (chartImage) {
        try {
          doc.setFontSize(FONTS.SECTION_TITLE);
          doc.setFont("SofiaSans-Bold", "bold");
          doc.setTextColor(0, 0, 0);
          doc.text("Overall Grade Component Breakdown", 20, yPosition);
          yPosition += SPACING.SECTION_TITLE;

          // Add the chart image with proper aspect ratio
          const tempImg = new Image();
          tempImg.src = chartImage;

          // Wait for image to load to get natural dimensions
          await new Promise((resolve) => {
            tempImg.onload = resolve;
            tempImg.onerror = resolve;
          });

          const aspectRatio = tempImg.naturalWidth / tempImg.naturalHeight;
          const maxWidth = 140;
          const imgWidth = maxWidth;
          const imgHeight = maxWidth / aspectRatio;
          const imgX = 20; // Left align

          doc.addImage(chartImage, 'PNG', imgX, yPosition, imgWidth, imgHeight);
          yPosition += imgHeight + SPACING.SECTION_GAP;
        } catch (error) {
          console.warn('Error adding chart image to PDF:', error);
        }
      }

      // =========================
      // Glossary Section
      // =========================
      yPosition = await checkNewPage(0, true); // Force new page for glossary

      doc.setFontSize(FONTS.SECTION_TITLE);
      doc.setFont("SofiaSans-Bold", "bold");
      doc.setTextColor(0, 0, 0);
      
      // Add glossary icon
      const iconPath = "assets/images/glossary-icon.png";
      const iconLoaded = await loadImage(iconPath);
      
      if (iconLoaded) {
        try {
          doc.addImage(iconLoaded, "PNG", 20, yPosition - 4, 6, 6);
          doc.text("Glossary", 30, yPosition);
        } catch (error) {
          console.warn('Error adding glossary icon:', error);
          doc.text("📖 Glossary", 20, yPosition);
        }
      } else {
        doc.text("📖 Glossary", 20, yPosition);
      }
      yPosition += SPACING.SECTION_TITLE + 2;

      const glossaryItems = [
        { term: "Trade area", definition: "The geographic region around a store defined by drive time (e.g., 5, 7, or 10 mins). It represents the area from which most customers are likely to travel to shop at the store." },
        { term: "Trade area average", definition: "The average performance of all stores located within the defined trade area. This provides a localized benchmark for comparison." },
        { term: "Market area", definition: "A broader geography based on the CBSA. A market area typically covers an entire metro region and includes multiple trade areas." },
        { term: "Market average", definition: " The average performance of all stores within the market area (CBSA). This provides a broad market benchmark for comparison." },
        { term: "Banner Market average", definition: "The average performance of all stores under the same banner (retail brand) within the market area. This shows how a specific chain is performing compared to the overall market." },
        { term: "Credit Score", definition: "RetailStat proprietary score reflecting the financial and operational strength of a store or site. Higher scores indicate lower risk and better stability." },
        { term: "Real Estate Quality", definition: "An assessment that balances factors such as Access (an assessment of ingress and egress), Parking conditions and sufficiency, Visibility of the store from the streets, Traffic Flow within the shopping center, and Shopping Center (SC) Condition (which is an assessment of the overall condition of the shopping center, including upkeep, maintenance, and visual appeal)." },
        { term: "Network Stability", definition: "Assessment of a banner's expansion and closure activity over time, indicating operational consistency and market commitment." },
        { term: "Market Presence", definition: "The concentration of a banner's stores within the subject market relative to its total network footprint." },
      ];

      doc.setFontSize(FONTS.TABLE_CONTENT);
      doc.setFont("Inter_18pt-Regular", "normal");
      doc.setTextColor(0, 0, 0);

      for (let i = 0; i < glossaryItems.length; i++) {
        const item = glossaryItems[i];

        // Check if we need a new page
        yPosition = await checkNewPage(20);

        // Term (bold) and definition on same line
        doc.setFont("SofiaSans-Bold", "bold");
        doc.setTextColor(0, 0, 0);
        const termText = `${item.term} - `;
        doc.text(termText, 20, yPosition);

        // Get term width to position definition
        const termWidth = doc.getTextWidth(termText);

        // Definition (normal) - handle multi-line properly
        doc.setFont("SofiaSans-Regular", "normal");
        doc.setTextColor(0, 0, 0);
        
        // Split definition for first line (reduced width)
        const firstLineText = doc.splitTextToSize(item.definition, pageWidth - 40 - termWidth)[0];
        doc.text(firstLineText, 20 + termWidth, yPosition);
        
        // Get remaining text after first line
        const words = item.definition.split(' ');
        const firstLineWords = firstLineText.split(' ');
        const remainingWords = words.slice(firstLineWords.length);
        
        if (remainingWords.length > 0) {
          const remainingText = remainingWords.join(' ');
          const remainingLines = doc.splitTextToSize(remainingText, pageWidth - 40);
          
          for (let j = 0; j < remainingLines.length; j++) {
            yPosition += 4;
            doc.text(remainingLines[j], 20, yPosition);
          }
        }

        const totalLines = remainingWords.length > 0 ? doc.splitTextToSize(item.definition, pageWidth - 40).length : 1;
        yPosition += totalLines > 1 ? 1 : 2;

        // Add thin grey horizontal line after each item
        if (i < glossaryItems.length - 1) {
          yPosition += 3;
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.2);
          doc.line(20, yPosition, pageWidth - 20, yPosition);
          yPosition += 4;
        }
      }

      // =========================
      // Add footers to all pages
      // =========================
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        await drawFooter(i, totalPages);
      }

      // Save PDF
      const storeName = this.assetReviewStoreDetails?.storeName ||
                       this.storeData?.targetName ||
                       "Analytics_Report";
      const sanitizedName = String(storeName).replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `${sanitizedName}_${currentDate}.pdf`;

      doc.save(fileName);

      console.log(`✅ PDF generated successfully: ${fileName}`);
      console.log(`📄 Total pages: ${totalPages}`);
      console.log(`🏪 Store: ${storeName}`);
      console.log(`🏆 Competitors included: ${competitorsData.length}`);
    };

    // Execute PDF generation
     generatePDFContent();

  } catch (error) {
    console.error("❌ Critical error generating PDF:", error);

    // User-friendly error message
    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
    alert(`Failed to generate PDF: ${errorMessage}\n\nPlease check your internet connection and try again. If the problem persists, contact support.`);

    // Re-throw for debugging in development
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      throw error;
    }
  }
}


  onDownloadPdf(): void {
    this.downloadPDF();
    this.downloadPdf.emit();
    const metadata = {
    storeId: this.store?.uuid,
      storeName: this.store?.name,
      cbsa : this.cbsa,
    downloadType: 'pdf'   // 👈 add download format to metadata
  };

  const actionType = USER_TRACKING_ACTIONS.ASSET_REVIEW_DOWNLOAD;
  const actionDescription = USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.ASSET_REVIEW_DOWNLOAD]('PDF');

  this.hitTrackingService
    .hitTheUserTracking(
      USER_TRACKING_SOURCES.ASSET_REVIEW,      // hit_source (you can rename if needed)
      metadata,            // returnedData
      actionType,          // action
      actionDescription,   // action_description
      true                 // is_key_action
    )
    .subscribe({
      next: res => console.log(`✅ Tracking success (pdf)`, res),
      error: err => console.error(`❌ Tracking failed (pdf)`, err)
    });
  }



private formatDateForCSV(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate().toString().padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear().toString().slice(-2);
    return `${day}-${month}-${year}`;
  } catch {
    // If date parsing fails, return as-is or default format
    return dateStr;
  }
}


  async downloadExcel(): Promise<void> {
  try {
    const workbook = new ExcelJS.Workbook();

    const now = new Date();
    const currentDate = `${(now.getMonth() + 1).toString().padStart(2, '0')}/${
      now.getDate().toString().padStart(2, '0')
    }/${now.getFullYear()}`;

    const applyTitleStyle = (cell: ExcelJS.Cell) => {
      cell.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F1B4D' } };
      cell.alignment = { horizontal: 'center' };
    };
    const applySectionStyle = (cell: ExcelJS.Cell) => {
      cell.font = { bold: true, size: 14 };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } };
    };
    const applyTableHeaderStyle = (cell: ExcelJS.Cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF853399' } };
    };

    // === EXECUTIVE SUMMARY SHEET (first tab) ===
    const summarySheet = workbook.addWorksheet('Executive Summary');
    summarySheet.columns = [{ width: 25 }, { width: 15 }, { width: 15 }];
    summarySheet.addRows([
      ['RetailStat Analytics - Executive Summary'],
      [`Generated: ${currentDate}`],
      [''],
      ['Key Metrics Overview'],
      ['Metric', 'Grade/Value'],
      ['Overall Performance', this.overallGrade || 'N/A'],
      ['Total Sales Performance', this.storePerformanceData.salesTotalPerformance.salesTotalGrade || 'N/A'],
      ['Sales Per Sq Ft', this.storePerformanceData.salesPerSqftPerformance.salesPerSqftGrade || 'N/A'],
      ['Real Estate Quality', this.realEstateQualityData?.grade || 'N/A'],
      ['Banner Strength', this.bannerStrengthData?.grade || 'N/A'],
      ['Competitive Impact', this.getSafeValue(this.competitiveImpactData?.competitiveImpactGrade)],
      [''],
      ['Store Details'],
      ['Store Name', this.assetReviewStoreDetails?.storeName || this.storeData.targetName || 'N/A'],
      ['Location', this.assetReviewStoreDetails?.address || this.storeData.address || 'N/A'],
      ['Trade Area', this.assetReviewStoreDetails?.tradeAreaName || this.storeData.tradeArea || 'N/A'],
      ['Report Date', this.assetReviewStoreDetails?.reportDate || this.storeData.reportDate || 'N/A']
    ]);
    applyTitleStyle(summarySheet.getCell('A1'));
    applySectionStyle(summarySheet.getCell('A4'));
    applySectionStyle(summarySheet.getCell('A13'));
    applyTableHeaderStyle(summarySheet.getCell('A5'));
    applyTableHeaderStyle(summarySheet.getCell('B5'));

    // === STORE INFORMATION SHEET ===
    const storeInfoSheet = workbook.addWorksheet('Store Information');
    storeInfoSheet.columns = [{ width: 20 }, { width: 40 }];
    storeInfoSheet.addRows([
      ['RetailStat Analytics - Performance Report'],
      [`Generated: ${currentDate}`],
      [''],
      ['Store Information'],
      ['Property', 'Value'],
      ['Store Name', this.assetReviewStoreDetails?.storeName || this.storeData.targetName || 'N/A'],
      ['Address', this.assetReviewStoreDetails?.address || this.storeData.address || 'N/A'],
      ['Trade Area', this.assetReviewStoreDetails?.tradeAreaName || this.storeData.tradeArea || 'N/A'],
      ['Report Date', this.assetReviewStoreDetails?.reportDate || this.storeData.reportDate || 'N/A'],
      ['Overall Store Grade', this.overallGrade || 'N/A'],
    ]);
    applyTitleStyle(storeInfoSheet.getCell('A1'));
    applySectionStyle(storeInfoSheet.getCell('A4'));
    applyTableHeaderStyle(storeInfoSheet.getCell('A5'));
    applyTableHeaderStyle(storeInfoSheet.getCell('B5'));

    // === PERFORMANCE METRICS SHEET ===
    const performanceSheet = workbook.addWorksheet('Performance Metrics');
    performanceSheet.columns = [{ width: 25 }, { width: 20 }];
    performanceSheet.addRows([
      ['Performance Metrics'],
      [''],
      ['Total Sales Performance'],
      ['Metric', 'Value'],
      ['Grade', this.storePerformanceData.salesTotalPerformance.salesTotalGrade || 'N/A'],
      ['Subject Store Annual Sales', this.storePerformanceData.salesTotalPerformance.annualSalesTotal || 'N/A'],
      [`vs Trade Area Average ${this.storePerformanceData?.driveTimeMinutes ? '(' + this.storePerformanceData?.driveTimeMinutes + ' min DT)' : ''}`, this.formatPercentage(this.storePerformanceData.salesTotalPerformance.vsTradeAreaSalesAverage) || 'N/A'],
      ['vs Banner Market Average', this.formatPercentage(this.storePerformanceData.salesTotalPerformance.vsBannerMarketSalesAverage) || 'N/A'],
      ['vs Market Average', this.formatPercentage(this.storePerformanceData.salesTotalPerformance.vsMarketSalesAverage) || 'N/A'],
      [''],
      ['Sales Per Square Foot'],
      ['Metric', 'Value'],
      ['Grade', this.storePerformanceData.salesPerSqftPerformance.salesPerSqftGrade || 'N/A'],
      ['Subject Store Annual Sales PSF', this.storePerformanceData.salesPerSqftPerformance.annualSalesPerSqft || 'N/A'],
      [`vs Trade Area Average ${this.storePerformanceData?.driveTimeMinutes ? '(' + this.storePerformanceData?.driveTimeMinutes + ' min DT)' : ''}`, this.formatPercentage(this.storePerformanceData.salesPerSqftPerformance.vsTradeAreaSalesPerSqftAverage) || 'N/A'],
      ['vs Banner Market Average', this.formatPercentage(this.storePerformanceData.salesPerSqftPerformance.vsBannerMarketSalesPerSqftAverage) || 'N/A'],
      ['vs Market Average', this.formatPercentage(this.storePerformanceData.salesPerSqftPerformance.vsMarketSalesPerSqftAverage) || 'N/A'],
      [''],
      ['Real Estate Quality'],
      ['Metric', 'Value'],
      ['Grade', this.realEstateQualityData?.grade || 'N/A'],
      ['Access', this.realEstateQualityData?.accessibility || 'N/A'],
      ['Parking', this.realEstateQualityData?.parking || 'N/A'],
      ['Visibility', this.realEstateQualityData?.visibility || 'N/A'],
      ['Traffic Flow', this.realEstateQualityData?.trafficFlow || 'N/A'],
      ['Shopping Center Condition', this.realEstateQualityData?.scCondition || 'N/A'],
      [''],
      ['Banner Strength'],
      ['Metric', 'Value'],
      ['Grade', this.bannerStrengthData?.grade || 'N/A'],
      ['Credit Score', this.bannerStrengthData?.creditRating || 'N/A'],
      ['Network Stability', this.bannerStrengthData?.networkStabillity || 'N/A'],
      ['Market Presence', this.bannerStrengthData?.marketPresence || 'N/A']
    ]);
    [1, 3, 11, 19, 28].forEach(r => applySectionStyle(performanceSheet.getCell(r, 1)));
    [4, 12, 20, 29].forEach(r => {
      applyTableHeaderStyle(performanceSheet.getCell(r, 1));
      applyTableHeaderStyle(performanceSheet.getCell(r, 2));
    });

    // === COMPETITIVE ANALYSIS SHEET ===
    const competitiveSheet = workbook.addWorksheet('Competitive Analysis');
    competitiveSheet.columns = [{ width: 25 }, { width: 12 }, { width: 45 }];
    const competitiveGrade = this.getSafeValue(this.competitiveImpactData?.competitiveImpactGrade, 'N/A');
    const competitiveRows: any[][] = [
      [`Competitive Impact${this.getAllCompetitors().length > 0 ? ` (${this.driveTimeRadiusMinutes} min DT)` : ''} - Grade: ${competitiveGrade}`],
      [`Drive Time :  ${this.driveTimeRadiusMinutes} minutes`],
      [''],
      ['Competitor', 'Distance', 'Address']
    ];
    this.getAllCompetitors().forEach(competitor => {
      competitiveRows.push([
        this.getSafeValue(competitor.name),
        this.getSafeValue(competitor.distance),
        this.getSafeValue(competitor.address)
      ]);
    });
    competitiveSheet.addRows(competitiveRows);
    applySectionStyle(competitiveSheet.getCell('A1'));
    applyTableHeaderStyle(competitiveSheet.getCell('A4'));
    applyTableHeaderStyle(competitiveSheet.getCell('B4'));
    applyTableHeaderStyle(competitiveSheet.getCell('C4'));

    // Download the file
    const fileName = `${this.assetReviewStoreDetails?.storeName || this.storeData.targetName || 'Analytics_Report'}_${currentDate.replace(/\//g, '-')}.xlsx`;
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);

    console.log('Excel file generated successfully:', fileName);

  } catch (error) {
    console.error('Error generating Excel file:', error);
    alert('Error generating Excel file. Please try again.');
  }
}
 downloadCSV(): void {
  try {
    // Prepare data array following the exact structure from your sample
    const csvData = [
      // Header row
      ['Section', 'Metric', 'Value'],

      // Store Info section
      ['Store Info', 'Store Name/ID', this.assetReviewStoreDetails?.storeName || this.storeData.targetName || 'N/A'],
      ['Store Info', 'Address', this.assetReviewStoreDetails?.address || this.storeData.address || 'N/A'],
      ['Store Info', 'Trade Area', this.assetReviewStoreDetails?.tradeAreaName || this.storeData.tradeArea || 'N/A'],
      ['Store Info', 'Report Date', this.formatDateForCSV(this.assetReviewStoreDetails?.reportDate || this.storeData.reportDate || 'N/A')],

      // Overall Grade section
      ['Overall Grade', 'Overall Store Grade', this.overallGrade || 'N/A'],

      // Total Sales Performance section
      ['Total Sales Performance', 'Grade', this.storePerformanceData.salesTotalPerformance.salesTotalGrade || 'N/A'],
      ['Total Sales Performance', 'Subject Store Annual Sales', this.storePerformanceData.salesTotalPerformance.annualSalesTotal || 'N/A'],
      ['Total Sales Performance', `vs Trade Area Average ${ this.storePerformanceData?.driveTimeMinutes ? '(' + this.storePerformanceData?.driveTimeMinutes + ' min DT)' : '' }`, this.formatPercentage(this.storePerformanceData.salesTotalPerformance.vsTradeAreaSalesAverage) || 'N/A'],
      ['Total Sales Performance', 'vs Banner Market Average', this.formatPercentage(this.storePerformanceData.salesTotalPerformance.vsBannerMarketSalesAverage) || 'N/A'],
      ['Total Sales Performance', 'vs Market Average', this.formatPercentage(this.storePerformanceData.salesTotalPerformance.vsMarketSalesAverage) || 'N/A'],
      
      // Sales Per Square Foot section
      ['Sales Per Square Foot', 'Grade', this.storePerformanceData.salesPerSqftPerformance.salesPerSqftGrade ||'N/A'],
      ['Sales Per Square Foot', 'Subject Store Annual Sales PSF', this.storePerformanceData.salesPerSqftPerformance.annualSalesPerSqft || 'N/A'],
      ['Sales Per Square Foot',`vs Trade Area Average ${ this.storePerformanceData?.driveTimeMinutes ? '(' + this.storePerformanceData?.driveTimeMinutes + ' min DT)' : '' }`, this.formatPercentage(this.storePerformanceData.salesPerSqftPerformance.vsTradeAreaSalesPerSqftAverage) || 'N/A'],
      ['Sales Per Square Foot', 'vs Banner Market Average', this.formatPercentage(this.storePerformanceData.salesPerSqftPerformance.vsBannerMarketSalesPerSqftAverage) || 'N/A'],
      ['Sales Per Square Foot', 'vs Market Average', this.formatPercentage(this.storePerformanceData.salesPerSqftPerformance.vsMarketSalesPerSqftAverage) || 'N/A'],
      

      // Real Estate Quality section
      ['Real Estate Quality', 'Grade',  this.realEstateQualityData?.grade || 'N/A' ],
      ['Real Estate Quality', 'Access',  this.realEstateQualityData?.accessibility || 'N/A' ],
      ['Real Estate Quality', 'Parking', this.realEstateQualityData?.parking || 'N/A' ],
      ['Real Estate Quality', 'Visibility',  this.realEstateQualityData?.visibility || 'N/A' ],
      ['Real Estate Quality', 'Traffic Flow',  this.realEstateQualityData?.trafficFlow || 'N/A' ],
      ['Real Estate Quality', 'Shopping Center Condition', this.realEstateQualityData?.scCondition || 'N/A'],

      // Banner Strength section
      ['Banner Strength', 'Grade', this.bannerStrengthData?.grade || 'N/A'],
      ['Banner Strength', 'Credit Score', this.bannerStrengthData?.creditRating || 'N/A'],
      ['Banner Strength', 'Network Stability', this.bannerStrengthData?.networkStabillity || 'N/A'],
      ['Banner Strength', 'Market Presence', this.bannerStrengthData?.marketPresence || 'N/A'],
      
      //Competitive Impact
      [`Competitive Impact${
    ` (${this.driveTimeRadiusMinutes} min DT)`
  }`,'Grade',this.getSafeValue(this.competitiveImpactData?.competitiveImpactGrade, 'N/A')]

    ];

    // Add Competitive Impact section from API data
    const competitors = this.getAllCompetitors();
    competitors.forEach((competitor, index) => {
      const competitorInfo = `${this.getSafeValue(competitor.name)} - ${this.getSafeValue(competitor.distance)}`;
      csvData.push([`Competitive Impact`, `Competitor ${index + 1}`, competitorInfo]);
    });

    // Convert array to CSV string
    const csvContent = csvData.map(row => {
      return row.map(cell => {
        // Handle cells that contain commas, quotes, or newlines
        const cellStr = String(cell || '');
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(',');
    }).join('\n');

    // Create and download the CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);

    const currentDate = new Date().toLocaleDateString().replace(/\//g, '-');
    const fileName = `${this.assetReviewStoreDetails?.storeName || this.storeData.targetName || 'Analytics_Report'}_${currentDate}.csv`;

    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);

  } catch (error) {
    console.error('Error generating CSV:', error);
    alert('Error generating CSV. Please try again.');
  }
}
  onExportData(type: string): void {
    if (type === 'csv') {
    this.downloadCSV();
  }
  if(type==='excel'){
    this.downloadExcel();
  }
    this.exportData.emit(type);
    this.exportMenuVisible = false;
    const metadata = {
    storeId: this.store?.uuid,
      storeName: this.store?.name,
      cbsa : this.cbsa,
    downloadType: type   // 👈 add download format to metadata
  };

  const actionType = USER_TRACKING_ACTIONS.ASSET_REVIEW_DOWNLOAD;
  const actionDescription = USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.ASSET_REVIEW_DOWNLOAD](type.toUpperCase());

  this.hitTrackingService
    .hitTheUserTracking(
      USER_TRACKING_SOURCES.ASSET_REVIEW,      // hit_source (you can rename if needed)
      metadata,            // returnedData
      actionType,          // action
      actionDescription,   // action_description
      true                 // is_key_action
    )
    .subscribe({
      next: res => console.log(`✅ Tracking success (${type})`, res),
      error: err => console.error(`❌ Tracking failed (${type})`, err)
    });
  }

  onExpandView(): void {
    this.expandView.emit();
  }

  onOpenExternal(): void {
    this.openExternal.emit();
  }

  toggleExportMenu(): void {
    this.exportMenuVisible = !this.exportMenuVisible;
  }

  toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
  }

  closeDropdown(): void {
    this.dropdownOpen = false;
  }

  download(format: string): void {
    if(format==='pdf'){
      this.downloadPDF();
    }
    if(format==='csv'){
      this.downloadCSV();
    }
    if(format==='excel'){
      this.downloadExcel();
    }
    this.exportData.emit(format);
    this.closeDropdown();
    const metadata = {
    storeId: this.store?.uuid,
      storeName: this.store?.name,
      cbsa : this.cbsa,
    downloadType: format   // 👈 add download format to metadata
  };

  const actionType = USER_TRACKING_ACTIONS.ASSET_REVIEW_DOWNLOAD;
  const actionDescription = USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.ASSET_REVIEW_DOWNLOAD](format.toUpperCase());

  this.hitTrackingService
    .hitTheUserTracking(
      USER_TRACKING_SOURCES.ASSET_REVIEW,      // hit_source (you can rename if needed)
      metadata,            // returnedData
      actionType,          // action
      actionDescription,   // action_description
      true                 // is_key_action
    )
    .subscribe({
      next: res => console.log(`✅ Tracking success (${format})`, res),
      error: err => console.error(`❌ Tracking failed (${format})`, err)
    });
  }

  expandPanel(): void {
    this.expandView.emit();
    const metadata = {
      storeId: this.store?.uuid,
      storeName: this.store?.name,
      cbsa : this.cbsa

    };
    console.log("🚀 ~ StoreSpotlightPanelComponent ~ handleAssetReview ~ metadata:", metadata)

    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.ASSET_REVIEW,
      metadata,
      USER_TRACKING_ACTIONS.ASSET_REVIEW_OPEN_EXPAND_VIEW,
      USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.ASSET_REVIEW_OPEN_EXPAND_VIEW],
      true
    ).subscribe({
      next: res => console.log('Tracking success:', res),
      error: err => console.error('Tracking failed:', err)
    });
  }

  openInNewPage(): void {
    this.openExternal.emit();
     const metadata = {
      storeId: this.store?.uuid,
      storeName: this.store?.name,
      cbsa : this.cbsa
    };
    console.log("🚀 ~ StoreSpotlightPanelComponent ~ handleAssetReview ~ metadata:", metadata)

    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.ASSET_REVIEW,
      metadata,
      USER_TRACKING_ACTIONS.ASSET_REVIEW_OPEN_NEW_TAB,
      USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.ASSET_REVIEW_OPEN_NEW_TAB],
      true
    ).subscribe({
      next: res => console.log('Tracking success:', res),
      error: err => console.error('Tracking failed:', err)
    });
  }

  assetReviewClosehandle(): void {
    this.assetReviewClose.emit();
  }

  toggleGradeBreakdown(): void {
    this.isGradeBreakdownExpanded = !this.isGradeBreakdownExpanded;

    // Close glossary if grade breakdown is being opened
    if (this.isGradeBreakdownExpanded && this.isGlossaryExpanded) {
      this.isGlossaryExpanded = false;
    }

    if (this.isGradeBreakdownExpanded) {
      const metadata = {
      storeId: this.store?.uuid,
      storeName: this.store?.name,
      cbsa : this.cbsa
    };
    console.log("🚀 ~ StoreSpotlightPanelComponent ~ handleAssetReview ~ metadata:", metadata)

    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.ASSET_REVIEW,
      metadata,
      USER_TRACKING_ACTIONS.ASSET_REVIEW_OVERALL_GRADE_OPEN,
      USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.ASSET_REVIEW_OVERALL_GRADE_OPEN],
      true
    ).subscribe({
      next: res => console.log('Tracking success:', res),
      error: err => console.error('Tracking failed:', err)
    });

    }
  }
   toggleGlossary(): void {
    this.isGlossaryExpanded = !this.isGlossaryExpanded;

    // Close grade breakdown if glossary is being opened
    if (this.isGlossaryExpanded && this.isGradeBreakdownExpanded) {
      this.isGradeBreakdownExpanded = false;
    }

    if(this.isGlossaryExpanded){
      const metadata = {
      storeId: this.store?.uuid,
      storeName: this.store?.name,
      cbsa : this.cbsa
    };
    console.log("🚀 ~ StoreSpotlightPanelComponent ~ handleAssetReview ~ metadata:", metadata)

    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.ASSET_REVIEW,
      metadata,
      USER_TRACKING_ACTIONS.ASSET_REVIEW_GLOSSARY_OPEN,
      USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.ASSET_REVIEW_GLOSSARY_OPEN],
      true
    ).subscribe({
      next: res => console.log('Tracking success:', res),
      error: err => console.error('Tracking failed:', err)
    });
    }
  }

  toggleCompetitorList(): void {
    this.isCompetitorListExpanded = !this.isCompetitorListExpanded;
  }

  getAllCompetitors(): any[] {
    if (this.competitiveImpactData?.competitiveImpactList?.length > 0) {
            this.driveTimeRadiusMinutes = this.competitiveImpactData?.driveTimeRadiusMinutes;
      return this.competitiveImpactData.competitiveImpactList.map((competitor: any) => ({
        name: competitor.storeName || 'N/A',
        distance: competitor.distance ? competitor.distance + ' m' : 'N/A',
        address: competitor.storeAddress || 'N/A'
      }));
    }else{
      this.driveTimeRadiusMinutes = this.competitiveImpactData?.driveTimeRadiusMinutes;
    }

    // Return empty array if no API data - no fallback static data
    return [];
  }

  getVisibleCompetitors(): any[] {
    const allCompetitors = this.getAllCompetitors();
    return this.isCompetitorListExpanded ? allCompetitors : allCompetitors.slice(0, this.maxVisibleCompetitors);
  }

  // Method to retry API calls - now handled by parent
  retryApiCalls(): void {
    // Emit retry event to parent to retry failed APIs
    this.retryAnalytics.emit();
    const metadata = {
    storeId: this.store?.uuid,
      storeName: this.store?.name,
      cbsa : this.cbsa
  };

  this.hitTrackingService
    .hitTheUserTracking(
      USER_TRACKING_SOURCES.ASSET_REVIEW,      // hit_source (you can rename if needed)
      metadata,            // returnedData
      USER_TRACKING_ACTIONS.ASSET_REVIEW_TRY_AGAIN,          // action
      USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.ASSET_REVIEW_TRY_AGAIN],   // action_description
      true                 // is_key_action
    )
    .subscribe({
      next: res => console.log(`✅ Tracking success `, res),
      error: err => console.error(`❌ Tracking failed `, err)
    });

  }

  // Method to handle go back action
  handleGoBack(): void {
    // Emit close event to parent component (different from retry)
    this.assetReviewClose.emit();
    const metadata = {
    storeId: this.store?.uuid,
      storeName: this.store?.name,
      cbsa : this.cbsa
  };

  this.hitTrackingService
    .hitTheUserTracking(
      USER_TRACKING_SOURCES.ASSET_REVIEW,      // hit_source (you can rename if needed)
      metadata,            // returnedData
      USER_TRACKING_ACTIONS.ASSET_REVIEW_GO_BACK,          // action
      USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.ASSET_REVIEW_GO_BACK],   // action_description
      true                 // is_key_action
    )
    .subscribe({
      next: res => console.log(`✅ Tracking success `, res),
      error: err => console.error(`❌ Tracking failed `, err)
    });
  }

  // Helper method to safely get value with fallback to N/A
  getSafeValue(value: any, fallback: string = 'N/A'): string {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  // Handle numbers
  const num = Number(value);
  if (!isNaN(num)) {
    return num.toFixed(2); // always 2 decimals
  }

  // Convert to string
  let str = String(value);

  // Convert to camel case if requested
    str = str.toLowerCase();
    str = str.charAt(0).toUpperCase() + str.slice(1);

  return str;
}


  // Helper method to format percentage values
  formatPercentage(value: any): string {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    const numValue = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(numValue)) {
      return 'N/A';
    }
    if (numValue === 0) {
      return `${numValue.toFixed(2)}%`;
    }
    const sign = numValue > 0 ? '+' : '';
    return `${sign}${numValue.toFixed(2)}%`;
  }

  // Helper method to format currency values
  formatCurrency(value: any): string {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    const numValue = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(numValue)) {
      return 'N/A';
    }
    // Always convert to millions
  const inMillions = numValue / 1_000_000;
  return `$${inMillions.toFixed(2)}M`;
  }
}
